import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ExperienceStatus } from '@utriper/core';
import arrayMutators from 'final-form-arrays';
import { Field, Form } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { AlertDialog, Switch } from '@utriper/core-ui';
import { Box, Button, IconButton, FormGroup, Grid2, InputLabel, Typography, CircularProgress } from '@mui/material';
import { useExperiences } from 'core/contexts/experienceV2';
import { DeleteOutline } from '@mui/icons-material';

const UpdateStatusForm = ({ open, handleClose = () => {}, status, experienceId = null, experienceIds = [] }) => {
    const { i18n, t } = useTranslation();
    const translate = (key, options) => t(`update_experience_status_form.${key}`, options);
    const { experiences, experience, updatingStatus, updateStatus } = useExperiences();
    const initialValues = useMemo(() => {
        const updateOptions = false;
        let experiences = [];
        if (Number.isInteger(experienceId)) {
            experiences.push({
                id: experienceId,
                updateOptions,
            });
        } else if (experienceIds?.length > 0) {
            experiences = [
                ...experienceIds.map((expId) => ({
                    id: expId,
                    updateOptions,
                })),
            ];
        }

        return { experiences, status };
    }, [experienceId, experienceIds, status]);
    const canRemove = useMemo(() => !Number.isInteger(experienceId) && experienceIds?.length > 0, [experienceId, experienceIds]);

    const handleSubmit = async ({ experiences, status }) => {
        await updateStatus({ experiences, status });
        handleClose();
    };

    return (
        <AlertDialog
            open={open}
            handleClose={handleClose}
            hideTitle
            hideActions
            maxWidth="sm"
            fullWidth
            dialogContent={
                <Form
                    key={`update-experience-status-form${experienceId ?? 0}`}
                    mutators={{ ...arrayMutators }}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    render={({ values, handleSubmit, form }) => (
                        <form key={`update-experience-status-form${experienceId ?? 0}`} onSubmit={handleSubmit} noValidate>
                            <Box component="section" py={2}>
                                {/* Title */}
                                <Grid2 container direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px">
                                    <Typography fontSize={20} fontWeight={900} sx={{ color: '#131313' }} noWrap>
                                        {translate('title')}
                                    </Typography>
                                    <Typography fontSize={16} fontWeight={500} sx={{ color: '#131313bf' }}>
                                        {translate('description', { status: ExperienceStatus.getTranslatedName(ExperienceStatus.status, status, i18n.language) })}
                                    </Typography>
                                </Grid2>

                                {/* Form */}
                                <Grid2 container width="100%" direction="column" justifyContent="flex-start" alignItems="flex-start" gap="8px" mt="24px">
                                    <FieldArray name="experiences">
                                        {({ fields }) => (
                                            <FormGroup sx={{ width: '100%', gap: 2 }}>
                                                {fields.length > 1 && (
                                                    <Grid2 size={12}>
                                                        <Switch
                                                            label={<Typography sx={{ fontSize: '16px', fontWeight: 400, color: '#131313' }}>{translate('update_all')}</Typography>}
                                                            checked={values.experiences.every((exp) => exp.updateOptions)}
                                                            onChange={(e, checked) => {
                                                                fields.forEach((name) => {
                                                                    form.change(`${name}.updateOptions`, checked);
                                                                });
                                                            }}
                                                            disabled={updatingStatus}
                                                        />
                                                    </Grid2>
                                                )}
                                                <Box sx={{ maxHeight: 320, overflowY: 'auto', width: '100%', mb: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    {fields.map((name, index) => {
                                                        let experienceData = experiences.find((exp) => exp.id === values?.experiences[index]?.id);

                                                        if (!experienceData?.name && experience?.id === values?.experiences[index]?.id) {
                                                            experienceData = experience;
                                                        }

                                                        return (
                                                            <Grid2 container spacing={1} alignItems="center" key={name}>
                                                                <Grid2 size={canRemove ? 11 : 12}>
                                                                    <InputLabel
                                                                        sx={{
                                                                            fontSize: '18px',
                                                                            fontWeight: 400,
                                                                            color: '#131313',
                                                                        }}
                                                                    >
                                                                        {experienceData?.name}
                                                                    </InputLabel>
                                                                    <Field type="checkbox" name={`${name}.updateOptions`} component={Switch} label={translate('update_option')} disabled={updatingStatus} />
                                                                </Grid2>

                                                                {/* Delete Button */}
                                                                {canRemove && (
                                                                    <Grid2 size={1} display="flex" justifyContent="flex-end">
                                                                        <IconButton aria-label="delete" disabled={fields?.length === 1} onClick={() => fields.remove(index)} size="small" color="error">
                                                                            <DeleteOutline />
                                                                        </IconButton>
                                                                    </Grid2>
                                                                )}
                                                            </Grid2>
                                                        );
                                                    })}
                                                </Box>
                                            </FormGroup>
                                        )}
                                    </FieldArray>
                                </Grid2>

                                {/* Action Buttons */}
                                <Grid2 container justifyContent={{ xs: 'center', md: 'flex-end' }} alignItems="center" gap="12px" mt="16px">
                                    <Button
                                        sx={{
                                            padding: '8px 14px',
                                            borderColor: '#13131340',
                                            fontWeight: 700,
                                            width: { xs: '100%', md: 'fit-content' },
                                            textTransform: 'none',
                                        }}
                                        variant="outlined"
                                        disabled={updatingStatus}
                                        onClick={(e) => {
                                            handleClose();
                                            return form.reset(e);
                                        }}
                                    >
                                        {t('common.cancel')}
                                    </Button>
                                    <Button
                                        sx={{
                                            padding: '8px 14px',
                                            backgroundColor: 'var(--color-utriper-blue)',
                                            fontWeight: 700,
                                            width: { xs: '100%', md: 'fit-content' },
                                            textTransform: 'none',
                                        }}
                                        loading={updatingStatus}
                                        loadingIndicator={
                                            <Box sx={{ display: 'flex' }}>
                                                <CircularProgress size={20} sx={{ color: '#131313bf' }} />
                                            </Box>
                                        }
                                        variant="contained"
                                        type="submit"
                                        onClick={(e) => {
                                            return handleSubmit(e);
                                        }}
                                    >
                                        {t('common.update')}
                                    </Button>
                                </Grid2>
                            </Box>
                        </form>
                    )}
                />
            }
        />
    );
};

export default UpdateStatusForm;
